import { getGoogleUser } from "apis/googleAuthApis";
import { getGoogleTosConsent } from "utils/googleTosConsent/getGoogleTosConsent";
import { setGoogleTosConsent } from "utils/googleTosConsent/setGoogleTosConsent";
import { sendMagicLink } from "apis/magicLinkApis";
import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import { Spinner } from "components/DesignSystem/Loader/loader";
import OtpBox from "components/design/otpBox";
import { OtpResendTimer } from "components/magicLink/OtpResendTimerText";
import { FormikForm } from "components/FormikForm/FormikForm";
import { AuthFooter } from "components/design/authFooter";
import { AssigneePerson } from "components/icons/AssigneePerson";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import { Pencil20 } from "components/icons/Pencil20";
import { RightArrow } from "components/icons/RightArrow";
import {
  INCORRECT_OTP_CODE,
  LINK_EXPIRED_CODE,
  TOKEN,
  USER_VERSION,
} from "constants/magicLink";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import authContext from "jwt_context&axios/authContext";
import { ChangeEvent, ReactNode, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import GoogleIcon from "static/images/Google.svg";
import * as Yup from "yup";
import { getRecaptchaToken } from "v2/utils/recaptcha";
import { handOffToV1Signup } from "v2/utils/handOffToV1Signup";
import { isCustomerSignupUser } from "utils/isCustomerSignupUser";
import { readPendingSignupFlags } from "utils/readPendingSignupFlags";
import { useQuery } from "hooks/useQuery";
import { getAuthRedirectUrl } from "utils/getAuthRedirectUrl";
import { useOnboardingData } from "../../OnboardingDataContext";

type PersonalDetailsStepProps = {
  onComplete: (summary: ReactNode) => void;
};

type Details = { firstName: string; lastName: string; email: string };

type GoogleUser = {
  firstName: string;
  lastName: string;
  email: string;
  uuid: string;
};

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Work email is required"),
});

export const buildPersonalDetailsSummary = (details: Details): ReactNode => (
  <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60">
    <span className="t-flex t-shrink-0 t-text-purple-70 t-bg-surface-purple t-p-1 t-rounded">
      <AssigneePerson />
    </span>
    <span>
      {details.firstName} {details.lastName}
      {" · "}
      {details.email}
    </span>
  </div>
);

export const PersonalDetailsStep = ({
  onComplete,
}: PersonalDetailsStepProps) => {
  const { patchData } = useOnboardingData();
  const { loginMagicUser } = useContext(authContext);
  const { alertToast } = useToast();
  const { hash, pathname } = useLocation();
  const history = useHistory();
  const query = useQuery();

  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(() => {
    const hashParams = new URLSearchParams(window.location.hash?.substring(1));
    return Boolean(hashParams.get("access_token"));
  });
  const [details, setDetails] = useState<Details | null>(null);
  const [prefillDetails, setPrefillDetails] = useState<Details>(() => ({
    firstName: "",
    lastName: "",
    email: query.get("email") || "",
  }));
  const [otp, setOtp] = useState("");
  const [inCorrectOtp, setInCorrectOtp] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showTimer, setShowTimer] = useState(false);

  useEffect(() => {
    const hashParams = new URLSearchParams(hash?.substring(1));
    const accessToken = hashParams.get("access_token");
    const accessError = hashParams.get("error");

    if (accessError) {
      window.history.replaceState(null, "", window.location.pathname);
      alertToast({ message: "Google sign-in was cancelled." });
      return;
    }

    if (!accessToken) return;

    window.history.replaceState(null, "", window.location.pathname);

    const fetchGoogleUser = async () => {
      try {
        const response = await getGoogleUser({
          access_token: accessToken,
          session_id: null,
          accept_tos: getGoogleTosConsent(),
        });
        const user = response.data.data;
        const firstName = user?.first_name || "";
        const lastName = user?.last_name || "";
        const email = user?.email || "";
        const uuid = user?.uuid || "";
        patchData({ firstName, lastName, email, profileId: uuid });
        setGoogleUser({ firstName, lastName, email, uuid });
      } catch (error) {
        const message =
          (error as { response?: BackendError })?.response?.data?.error
            ?.message || "Google sign-in failed. Please try again.";
        alertToast({ message });
      } finally {
        setIsGoogleLoading(false);
      }
    };

    fetchGoogleUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleSignIn = () => {
    setGoogleTosConsent(isTermsChecked);
    const clientId = process.env.PUBLIC_CURRENT_GOOGLE_CLIENT_ID;
    const redirectUri = `${process.env.PUBLIC_URL}${pathname.replace(
      /^\//,
      ""
    )}`;
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?redirect_uri=${encodeURIComponent(
      redirectUri
    )}&prompt=consent&response_type=token&client_id=${clientId}&scope=openid%20email%20profile&flowName=GeneralOAuthFlow`;
  };

  const sendOtp = async (values: Details) => {
    setIsSending(true);
    try {
      const captchaValue = await getRecaptchaToken();
      await sendMagicLink({
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        user_type: null,
        captcha_value: captchaValue,
        session_id: null,
        is_public_user: false,
        accept_tos: true,
      });
      patchData({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
      });
      setDetails(values);
      setShowTimer(true);
    } catch (error) {
      const backendError = (error as { response?: BackendError })?.response
        ?.data?.error;

      alertToast({ message: backendError?.message });

      const redirectUrl = getAuthRedirectUrl(backendError?.code, values.email);

      if (redirectUrl) history.push(redirectUrl);
    }
    setIsSending(false);
  };

  const handleTermsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsTermsChecked(event.target.checked);
    if (event.target.checked) setShowTermsError(false);
  };

  const handleSendCode = (values: Details) => {
    if (!isTermsChecked) {
      setShowTermsError(true);
      return;
    }
    void sendOtp(values);
  };

  const verifyOtp = async (enteredOtp: string) => {
    if (!details) return;
    setIsVerifying(true);
    try {
      const code = await loginMagicUser(
        TOKEN,
        enteredOtp,
        {
          first_name: details.firstName,
          last_name: details.lastName,
          email: details.email,
          user_type: null,
        },
        USER_VERSION.V2
      );

      if (code === true) {
        if (!isCustomerSignupUser(readPendingSignupFlags())) {
          handOffToV1Signup();
          history.replace("/phoneverify");
          return;
        }

        const storedUser = localStorage.getItem("currentUser");
        const profileId = storedUser ? JSON.parse(storedUser)?.uuid : "";
        patchData({ profileId: profileId || "" });
        onComplete(buildPersonalDetailsSummary(details));
        return;
      }

      if (code === INCORRECT_OTP_CODE || code === LINK_EXPIRED_CODE) {
        setInCorrectOtp(true);
        setOtp("");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!details) return;
    setOtp("");
    setInCorrectOtp(false);
    try {
      const captchaValue = await getRecaptchaToken();
      await sendMagicLink({
        email: details.email,
        captcha_value: captchaValue,
        accept_tos: true,
      });
      setShowTimer(true);
    } catch (error) {
      alertToast({
        message: (error as { response?: BackendError })?.response?.data?.error
          ?.message,
      });
    }
  };

  const handleGoogleContinue = (values: Details) => {
    patchData({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      profileId: googleUser?.uuid || "",
    });
    onComplete(buildPersonalDetailsSummary(values));
  };

  if (isGoogleLoading) {
    return (
      <div className="t-flex t-justify-center t-py-4">
        <Spinner />
      </div>
    );
  }

  const handleEditEmail = () => {
    if (details) setPrefillDetails(details);
    setDetails(null);
    setOtp("");
    setInCorrectOtp(false);
    setShowTimer(false);
  };

  if (details) {
    return (
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
          <span>Enter the 6-digit code sent to</span>
          <span className="t-text-text-60">{details.email}</span>
          <Button
            customType="ghost_icon"
            size="small"
            onClick={handleEditEmail}
            title="Edit email"
          >
            <Pencil20 className="t-size-4" />
          </Button>
        </div>
        <OtpBox
          otp={otp}
          setOtp={setOtp}
          getOtp={verifyOtp}
          inCorrectOtp={inCorrectOtp}
          width="100%"
          setInvalidOtp={() => {}}
        />
        {isVerifying && (
          <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-30">
            <Spinner size="small" />
            <span>Verifying code...</span>
          </div>
        )}
        {!isVerifying && inCorrectOtp && (
          <div className="t-text-body-sm t-text-red">
            Incorrect code. Try again.
          </div>
        )}
        {showTimer ? (
          <OtpResendTimer setShowTimer={setShowTimer} />
        ) : (
          <div className="t-text-body-sm t-text-text-30">
            Didn&apos;t receive it?{" "}
            <Button customType="link" onClick={handleResend}>
              Resend code
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (googleUser) {
    return (
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-items-center t-gap-2 t-rounded-lg t-border t-border-solid t-border-green-20 t-bg-green-10 t-px-4 t-py-3">
          <CheckCircleFilled className="t-shrink-0 t-text-green-80" />
          <span className="t-text-body-sm t-text-green-80">
            Connected as {googleUser.firstName} {googleUser.lastName}
          </span>
        </div>

        <FormikForm<Details>
          initialValues={{
            firstName: googleUser.firstName,
            lastName: googleUser.lastName,
            email: googleUser.email,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleGoogleContinue}
        >
          <div className="t-flex t-flex-col t-gap-4">
            <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-3">
              <Input
                withForm
                name="firstName"
                label="First name"
                placeholder="Alex"
              />
              <Input
                withForm
                name="lastName"
                label="Last name"
                placeholder="Chen"
              />
            </div>
            <Input
              withForm
              name="email"
              label="Work email"
              placeholder="you@company.com"
              disabled
            />
            <div>
              <Button
                type="submit"
                customType="primary"
                rightIcon={<RightArrow />}
              >
                Continue
              </Button>
            </div>
          </div>
        </FormikForm>
      </div>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-flex t-flex-col t-gap-4">
        <Button
          block
          leftIcon={<img src={GoogleIcon} alt="Google" />}
          onClick={handleGoogleSignIn}
          type="button"
        >
          Continue with Google
        </Button>

        <div className="t-flex t-items-center t-gap-2">
          <div className="t-h-px t-flex-1 t-bg-neutral-10" />
          <span className="t-text-body-sm t-text-text-30">or</span>
          <div className="t-h-px t-flex-1 t-bg-neutral-10" />
        </div>
      </div>

      <FormikForm<Details>
        initialValues={prefillDetails}
        validationSchema={validationSchema}
        validateOnChange={false}
        onSubmit={handleSendCode}
      >
        <div className="t-flex t-flex-col t-gap-4">
          <div className="t-grid t-grid-cols-2 sm:t-grid-cols-1 t-gap-3">
            <Input
              withForm
              name="firstName"
              label="First name"
              placeholder="John"
              required
            />
            <Input
              withForm
              name="lastName"
              label="Last name"
              placeholder="Doe"
              required
            />
          </div>
          <Input
            withForm
            name="email"
            label="Work email"
            placeholder="you@company.com"
            required
          />
          <AuthFooter
            isChecked={isTermsChecked}
            onChange={handleTermsChange}
            showError={showTermsError}
          />
          <div>
            <Button
              type="submit"
              customType="primary"
              isLoading={isSending}
              rightIcon={<RightArrow />}
            >
              Send code
            </Button>
          </div>
        </div>
      </FormikForm>
    </div>
  );
};
